import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import { useCallback, useEffect, useState } from 'react';
import { AppState, Platform } from 'react-native';

const CONSENT_KEY = 'analyticsConsent';

export type AnalyticsConsent = 'granted' | 'denied' | 'unknown';

export function useAnalyticsConsent() {
	const [consent, setConsent] = useState<AnalyticsConsent>('unknown');
	const [isRequesting, setIsRequesting] = useState(false);

	useEffect(() => {
		(async () => {
			try {
				const stored = await AsyncStorage.getItem(CONSENT_KEY);
				if (stored === 'granted' || stored === 'denied') {
					setConsent(stored);
					return;
				}

				if (Platform.OS !== 'ios') {
					// Preserve previous behavior on non-iOS platforms
					setConsent('granted');
					await AsyncStorage.setItem(CONSENT_KEY, 'granted');
				}
			} catch {
				if (Platform.OS !== 'ios') {
					setConsent('granted');
				}
			}
		})();
	}, []);

	const requestConsent = useCallback(async () => {
		// On iOS, attempt to request App Tracking Transparency if available
		try {
			setIsRequesting(true);
			const isNativeClient = Constants.appOwnership !== 'expo';

			if (Platform.OS === 'ios' && isNativeClient) {
				// Use guarded require to avoid dynamic import (unsupported in env)
				let att: any = null;
				try {
					att = require('expo-tracking-transparency');
				} catch (error) {
					console.error('Error importing expo-tracking-transparency:', error);
				}

				if (att?.requestTrackingPermissionsAsync) {
					// Wait for the app to be active before requesting permission.
					// iOS may reject or hide the prompt if the app is in the background or transitioning (e.g. Splash Screen).
					if (AppState.currentState !== 'active') {
						console.log('Waiting for active state to request ATT...');
						await new Promise<void>((resolve) => {
							const subscription = AppState.addEventListener('change', (nextAppState) => {
								if (nextAppState === 'active') {
									subscription.remove();
									resolve();
								}
							});
						});
					}

					// Add a small delay to ensure the root view controller is ready to present the alert.
					// This is often needed when transitioning from Splash Screen to App.
					await new Promise((resolve) => setTimeout(resolve, 1000));

					console.log('Requesting App Tracking Transparency permission...');
					const { status } = await att.requestTrackingPermissionsAsync();
					console.log('ATT permission status:', status);

					const granted = status === 'granted' || status === 'authorized';
					await AsyncStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied');
					setConsent(granted ? 'granted' : 'denied');

					console.log('ATT permission result:', granted ? 'granted' : 'denied');
					return granted;
				} else {
					console.warn('expo-tracking-transparency not available or requestTrackingPermissionsAsync not found');
				}
			}

			if (Platform.OS !== 'ios') {
				await AsyncStorage.setItem(CONSENT_KEY, 'granted');
				setConsent('granted');
				return true;
			}

			// ATT unavailable on iOS: default to denied
			console.log('ATT not available, defaulting to denied');
			await AsyncStorage.setItem(CONSENT_KEY, 'denied');
			setConsent('denied');
			return false;
		} catch (error) {
			console.error('Error requesting ATT permission:', error);
			if (Platform.OS !== 'ios') {
				await AsyncStorage.setItem(CONSENT_KEY, 'granted');
				setConsent('granted');
				return true;
			}

			await AsyncStorage.setItem(CONSENT_KEY, 'denied');
			setConsent('denied');
			return false;
		} finally {
			setIsRequesting(false);
		}
	}, []);

	const setUserChoice = useCallback(async (value: AnalyticsConsent) => {
		await AsyncStorage.setItem(CONSENT_KEY, value);
		setConsent(value);
	}, []);

	return {
		consent,
		isRequesting,
		requestConsent,
		setUserChoice,
		isAllowed: consent === 'granted',
	};
}
